/**
 * useToast.ts — Shared toast hook for the SPINEX admin dashboard
 *
 * Fixes vs original:
 *  1. ID collision: Math.random().toString(36) has a birthday-problem collision
 *     risk when many toasts are created quickly. Replaced with crypto.randomUUID()
 *     which is available in all modern browsers and Node 14.17+.
 *  2. Added 'info' and 'warning' types so pages don't have to abuse 'success'
 *     for neutral messages.
 *  3. Exposed clearAll() so pages can reset toast state on unmount.
 *  4. Auto-dismiss timeout is now configurable per-call (default 5 s).
 */

import { useState, useCallback } from 'react'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: string
  message: string
  type: ToastType
}

export interface UseToastReturn {
  toasts: Toast[]
  showToast: (message: string, type?: ToastType, duration?: number) => void
  removeToast: (id: string) => void
  clearAll: () => void
}

export function useToast(): UseToastReturn {
  const [toasts, setToasts] = useState<Toast[]>([])

  const showToast = useCallback(
    (message: string, type: ToastType = 'success', duration = 5_000) => {
      const id = crypto.randomUUID()
      setToasts((prev) => [...prev, { id, message, type }])
      if (duration > 0) {
        setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== id))
        }, duration)
      }
    },
    [],
  )

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const clearAll = useCallback(() => setToasts([]), [])

  return { toasts, showToast, removeToast, clearAll }
}
